export type { ApiConfig } from "./base/generated";
export * from "./base/index";
export * from "./custom/index";

import type { ResponseCode } from "@gosaas/types";
import { Api } from "./base/generated";
import { CustomApi } from "./custom/generated";
import { mergeTraceHeaders } from "./w3c-trace";

// ─── Token storage ────────────────────────────────────────────────────────────

export interface TokenStorage {
	getAccessToken(): string | null | Promise<string | null>;
	getRefreshToken(): string | null | Promise<string | null>;
	setAccessToken(token: string): void | Promise<void>;
	setRefreshToken(token: string): void | Promise<void>;
	deleteAccessToken(): void | Promise<void>;
	deleteRefreshToken(): void | Promise<void>;
}

// ─── Error classes ────────────────────────────────────────────────────────────

export class ApiError extends Error {
	constructor(
		message: string,
		public status?: number,
		public data?: unknown,
		public code?: string,
	) {
		super(message);
		this.name = "ApiError";
	}
}

/** Thrown by requestWithHandling for errors that reach the caller. */
export class TypedClientApiError extends Error {
	constructor(
		message: string,
		public status?: number,
		public data?: unknown,
		public code?: string,
	) {
		super(message);
		this.name = "TypedClientApiError";
	}
}

// ─── Response wrapper ─────────────────────────────────────────────────────────

export interface ApiResponse<T> {
	data?: T;
	message?: string;
	code: ResponseCode | string;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export interface ApiClientConfig {
	baseUrl: string;
	storage: TokenStorage;
	/** Called when the session cannot be refreshed (no token or refresh fails). */
	onUnauthorized?: () => void;
	/** Called when the server returns RATE_LIMIT_EXCEEDED. */
	onRateLimit?: () => void;
	/** Called when the server returns UNAUTHORIZED (permission denied). */
	onAccessDenied?: () => void;
	/** Optional extra trace headers (e.g. sentry-trace, baggage). W3C traceparent is always sent. */
	getTraceHeaders?: () => Record<string, string> | undefined;
	/** Called when the API response includes X-Request-ID. */
	onRequestId?: (requestId: string) => void;
}

const RATE_LIMIT_MESSAGE = "Failed to check rate limit";
const RATE_LIMIT_RETRIES = 2;
const RATE_LIMIT_RETRY_DELAY_MS = 250;
const RETRIEVE_ACCESS_TOKEN_PATH = "/api/security/retrieve-access-token";

function isInvalidTokenHttpError(error: unknown): boolean {
	const err = error as { status?: number; error?: { code?: string } };
	return err?.status === 401 && err?.error?.code === "INVALID_TOKEN";
}

function shouldAutoRefreshToken(
	path: string,
	secure: boolean | undefined,
): boolean {
	if (secure !== true) return false;
	if (path === RETRIEVE_ACCESS_TOKEN_PATH) return false;
	return true;
}

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function createRequestId(): string {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

type BaseApiInstance = InstanceType<typeof Api>;
type CustomApiInstance = InstanceType<typeof CustomApi>;

/** Base dashboard routes plus custom OpenAPI routes merged into `api`. */
export type MergedApiRoutes = BaseApiInstance["api"] & CustomApiInstance["api"];

export type ApiClient = BaseApiInstance & {
	api: MergedApiRoutes;
};

type HttpRequest = BaseApiInstance["request"];

function attachAutoTokenRefresh(
	httpClient: Pick<BaseApiInstance, "request">,
	refreshAccessToken: () => Promise<string>,
): void {
	const originalRequest = httpClient.request.bind(httpClient);

	httpClient.request = (async (params) => {
		try {
			return await originalRequest(params);
		} catch (error) {
			if (
				isInvalidTokenHttpError(error) &&
				shouldAutoRefreshToken(params.path ?? "", params.secure)
			) {
				await refreshAccessToken();
				return await originalRequest(params);
			}
			throw error;
		}
	}) as HttpRequest;
}

/** Merge custom `/api/*` routes from swagger-custom onto the dashboard client `api`. */
function mergeCustomRoutesOntoApi(
	api: BaseApiInstance["api"],
	custom: CustomApiInstance,
): void {
	const routes = custom.api;
	if (!routes) return;
	for (const [routeKey, routeFn] of Object.entries(routes)) {
		if (typeof routeFn === "function") {
			(api as Record<string, unknown>)[routeKey] = (
				routeFn as (...args: unknown[]) => unknown
			).bind(custom);
		}
	}
}

/** Attach other custom top-level namespaces without replacing `client.api`. */
function mergeCustomRouteNamespaces(
	client: BaseApiInstance,
	custom: CustomApiInstance,
): void {
	for (const key of Object.keys(custom)) {
		if (key === "baseUrl" || key === "api") {
			continue;
		}
		const namespace = (custom as unknown as Record<string, unknown>)[key];
		if (
			!namespace ||
			typeof namespace !== "object" ||
			Array.isArray(namespace)
		) {
			continue;
		}
		const bound: Record<string, unknown> = {};
		let hasRoutes = false;
		for (const [routeKey, routeFn] of Object.entries(namespace)) {
			if (typeof routeFn === "function") {
				bound[routeKey] = routeFn.bind(custom);
				hasRoutes = true;
			}
		}
		if (hasRoutes) {
			(client as unknown as Record<string, unknown>)[key] = bound;
		}
	}
}

export function createApiClient(config: ApiClientConfig) {
	const {
		baseUrl,
		storage,
		onUnauthorized,
		onRateLimit,
		onAccessDenied,
		getTraceHeaders,
		onRequestId,
	} = config;

	const instrumentedFetch: typeof fetch = async (input, init) => {
		const headers = new Headers(init?.headers);
		if (!headers.has("X-Request-ID")) {
			headers.set("X-Request-ID", createRequestId());
		}
		const traceHeaders = mergeTraceHeaders(getTraceHeaders?.());
		for (const [key, value] of Object.entries(traceHeaders)) {
			if (value && !headers.has(key)) {
				headers.set(key, value);
			}
		}

		const response = await fetch(input, { ...init, headers });
		const requestId = response.headers.get("X-Request-ID");
		if (requestId) {
			onRequestId?.(requestId);
		}
		return response;
	};

	const securityWorker = async () => {
		const token = await storage.getAccessToken();
		return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
	};

	const base = new Api({
		baseUrl,
		securityWorker,
		customFetch: instrumentedFetch,
	});
	const custom = new CustomApi({
		baseUrl,
		securityWorker,
		customFetch: instrumentedFetch,
	});
	mergeCustomRoutesOntoApi(base.api, custom);
	mergeCustomRouteNamespaces(base, custom);
	const client = base as ApiClient;

	let refreshPromise: Promise<string> | null = null;

	async function refreshAccessToken(): Promise<string> {
		if (refreshPromise) return refreshPromise;

		refreshPromise = (async () => {
			const refreshToken = await storage.getRefreshToken();
			if (!refreshToken) {
				await storage.deleteAccessToken();
				await storage.deleteRefreshToken();
				onUnauthorized?.();
				throw new TypedClientApiError(
					"Session expired. Please log in again.",
					401,
					null,
					"INVALID_TOKEN",
				);
			}

			try {
				const response = await client.api.securityRetrieveAccessTokenCreate({
					token: refreshToken,
				});

				const newToken = (response.data as any)?.data?.access_token;
				if (!newToken || typeof newToken !== "string") {
					await storage.deleteAccessToken();
					await storage.deleteRefreshToken();
					onUnauthorized?.();
					throw new TypedClientApiError(
						(response.data as any)?.message || "Failed to refresh session.",
						(response as any)?.status,
						response.data,
						(response.data as any)?.code,
					);
				}

				await storage.setAccessToken(newToken);
				const rotatedRefresh = (response.data as any)?.data?.refresh_token;
				if (rotatedRefresh && typeof rotatedRefresh === "string") {
					await storage.setRefreshToken(rotatedRefresh);
				}
				return newToken;
			} catch (error: unknown) {
				if (error instanceof TypedClientApiError) throw error;
				await storage.deleteAccessToken();
				await storage.deleteRefreshToken();
				onUnauthorized?.();
				throw error;
			} finally {
				refreshPromise = null;
			}
		})();

		return refreshPromise;
	}

	attachAutoTokenRefresh(base, refreshAccessToken);
	attachAutoTokenRefresh(custom, refreshAccessToken);

	async function requestWithHandling<T>(
		requestFn: () => Promise<unknown>,
	): Promise<ApiResponse<T>> {
		const unwrap = (res: unknown): ApiResponse<T> => ({
			data: (res as any)?.data?.data,
			message: (res as any)?.data?.message,
			code: (res as any)?.data?.code,
		});

		try {
			return unwrap(await requestFn());
		} catch (error: unknown) {
			const err = error as any;
			const statusCode: number | undefined = err?.status;
			const responseData = err?.error;

			// Raw network/runtime error (TypeError: Failed to fetch, CORS, offline, etc.)
			if (responseData === undefined && statusCode === undefined) {
				const message =
					err instanceof Error && err.message
						? err.message
						: "An unknown error occurred.";
				throw new TypedClientApiError(message, undefined, null, undefined);
			}

			const errorCode: string | undefined = responseData?.code;
			const errorMessage: string =
				responseData?.message ?? err?.message ?? "An unexpected error occurred";

			if (isInvalidTokenHttpError(err)) {
				await refreshAccessToken();
				return unwrap(await requestFn());
			}

			if (statusCode === 500 && errorMessage.includes(RATE_LIMIT_MESSAGE)) {
				for (let i = 0; i < RATE_LIMIT_RETRIES; i++) {
					await sleep(RATE_LIMIT_RETRY_DELAY_MS);
					try {
						return unwrap(await requestFn());
					} catch (retryError: unknown) {
						const retryErr = retryError as {
							status?: number;
							error?: { code?: string };
						};
						if (
							retryErr?.status === 401 &&
							retryErr?.error?.code === "INVALID_TOKEN"
						) {
							await refreshAccessToken();
						}
					}
				}
			}

			if (errorCode === "RATE_LIMIT_EXCEEDED") onRateLimit?.();
			if (errorCode === "UNAUTHORIZED") onAccessDenied?.();

			throw new TypedClientApiError(
				errorMessage,
				statusCode,
				responseData?.data ?? null,
				errorCode,
			);
		}
	}

	return { client, requestWithHandling };
}
